/**
 * reportAnalysis — 종합 분석 보고서(문의 #153) 2·3단계의 결정론 계산. LLM·외부 통계 없음.
 *   ① parsePsr      : 원산지결정기준(관세청 FTA 포털 원문) → 선택지별 기준(CC·CTH·CTSH·RVC·WO)
 *   ② checkOrigin   : BOM(재료 세번·원산지·금액)+제품 가격 → 협정별 충족·미충족·판정 불가 + 걸리는 재료·조달 대안
 *   ③ matchRequirement : 세관장확인 요건 대상 문구(● 항목)와 품명의 겹치는 말 — 해당 여부 판정 아님
 *   ④ parseExempt   : 관세청 감면 셀("용도세율 [세종] … [용도·규격] … [사후관리] Y") → 항목
 *   누적·미소기준·직접운송·역내가공 등은 계산하지 않는다(보고서가 밝힌다). 원문을 해석하지 못하면 '판정 불가'.
 */

// 보고서가 다루는 협정 — 당사국은 data/fta_agreements.json 의 members(한국 포함)와 같다.
const ASEAN10 = ["브루나이", "캄보디아", "인도네시아", "라오스", "말레이시아", "미얀마", "필리핀", "싱가포르", "베트남", "태국"];
export const REPORT_AGREEMENTS = [
  { key: "sg", name: "한·싱가포르 FTA", ftaId: "KORSG", rateCode: "FSG1", members: ["한국", "싱가포르"] },
  { key: "asean", name: "한·아세안 FTA", ftaId: "KORAS", rateCode: "FAS1", members: ["한국", ...ASEAN10] },
  { key: "rcep", name: "RCEP", ftaId: "KORRC", rateCode: "FRCAS1", members: ["한국", ...ASEAN10, "중국", "일본", "호주", "뉴질랜드"] },
];
export const COUNTRY_OPTIONS = Array.from(new Set(["한국", ...ASEAN10, "중국", "일본", "호주", "뉴질랜드", "미국", "독일", "대만", "인도", "기타"]));

const KIND_LABEL = { WO: "완전생산", CC: "류 변경(2단위)", CTH: "호 변경(4단위)", CTSH: "소호 변경(6단위)", RVC: "부가가치" };
const SHIFT_DIGITS = { CC: 2, CTH: 4, CTSH: 6 };

function kindsIn(text) {
  const t = String(text || "");
  const kinds = [];
  if (/완전(?:히)?\s*(?:생산|획득)/.test(t)) kinds.push({ type: "WO" });
  if (/다른\s*류|2단위\s*세번\s*변경|류\s*변경/.test(t)) kinds.push({ type: "CC" });
  else if (/다른\s*호|4단위\s*세번\s*변경|호\s*변경/.test(t)) kinds.push({ type: "CTH" });
  else if (/다른\s*소호|6단위\s*세번\s*변경|소호\s*변경/.test(t)) kinds.push({ type: "CTSH" });
  const rvc = t.match(/(\d{1,3}(?:\.\d+)?)\s*(?:%|퍼센트)\s*이상/);
  if (rvc && /부가가치/.test(t)) kinds.push({ type: "RVC", pct: Number(rvc[1]) });
  return kinds;
}

/** 원산지결정기준 원문 → { options: [[kind…]…], exceptions, parsed }. 선택지 사이는 OR, 선택지 안은 AND. */
export function parsePsr(text) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return { options: [], exceptions: false, parsed: false };
  let items = [t];
  if (/어느\s*하나/.test(t)) {
    const parts = t.split(/(?:^|\s)\d{1,2}\.\s/).slice(1).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) items = parts;
  }
  const options = [];
  for (const it of items) {
    // '다만 … 한정' 은 같은 선택지의 추가 조건(AND), '또는' 은 선택지 나누기(OR).
    const [main, proviso] = it.split(/다만[,\s]/);
    const ors = proviso ? [main] : main.split(/\s또는\s|\s혹은\s/);
    for (const o of ors) {
      const k = kindsIn(o);
      const extra = proviso ? kindsIn(proviso) : [];
      const all = [...k, ...extra.filter((e) => !k.some((x) => x.type === e.type))];
      if (all.length) options.push(all);
    }
  }
  const exceptions = /제외|제\s*\d{2}(?:\.\d{2})?\s*(?:류|호)의\s*(?:재료|것)/.test(t);
  return { options, exceptions, parsed: options.length > 0 };
}

export function kindText(k) {
  return k.type === "RVC" ? `부가가치 ${k.pct}% 이상` : KIND_LABEL[k.type] || k.type;
}

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,\s]/g, ""));
  return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null;
};
const digits = (v) => String(v || "").replace(/\D/g, "");

/**
 * BOM 원산지 판정. bom=[{name, hs, country, amount}], price=제품 가격(재료와 같은 통화).
 * 반환 { status: "met"|"not_met"|"unknown"|"no_rule", conditional, option, lines[], blocking[], suggestion }
 */
export function checkOrigin({ hsk, price, bom, psrText, members }) {
  const rule = parsePsr(psrText);
  if (!rule.parsed) return { status: "no_rule", lines: ["원산지결정기준 원문을 기준 유형으로 풀지 못했습니다 — 원문을 직접 확인하세요."], blocking: [] };
  const product = digits(hsk);
  const isOrig = (m) => members.includes(String(m.country || "").trim());
  const rows = (bom || []).filter((m) => m && (m.name || m.hs || m.amount));
  const nonOrig = rows.filter((m) => !isOrig(m));
  const P = num(price);

  const evalKind = (k) => {
    if (k.type === "WO") return nonOrig.length === 0 ? { ok: true, why: "비원산지 재료 없음" } : { ok: false, why: `비원산지 재료 ${nonOrig.length}개`, blocking: nonOrig };
    if (SHIFT_DIGITS[k.type]) {
      const n = SHIFT_DIGITS[k.type];
      if (product.length < n) return { ok: null, why: "제품 세번 자릿수 부족" };
      const missing = nonOrig.filter((m) => digits(m.hs).length < n);
      const same = nonOrig.filter((m) => digits(m.hs).length >= n && digits(m.hs).slice(0, n) === product.slice(0, n));
      if (same.length) return { ok: false, why: `제품과 ${n}단위가 같은 비원산지 재료 ${same.length}개`, blocking: same };
      if (missing.length) return { ok: null, why: `비원산지 재료 ${missing.length}개의 세번(${n}단위 이상) 없음` };
      return { ok: true, why: `비원산지 재료가 모두 ${n}단위 세번이 다름` };
    }
    if (k.type === "RVC") {
      if (!P || P <= 0) return { ok: null, why: "제품 가격 없음" };
      if (nonOrig.some((m) => num(m.amount) == null)) return { ok: null, why: "비원산지 재료 금액 빠짐" };
      const vnm = nonOrig.reduce((s, m) => s + num(m.amount), 0);
      const rvc = ((P - vnm) / P) * 100;
      const maxVnm = P * (1 - k.pct / 100);
      const r = Math.round(rvc * 10) / 10;
      return rvc >= k.pct
        ? { ok: true, why: `부가가치 ${r}% (기준 ${k.pct}%)` }
        : { ok: false, why: `부가가치 ${r}% < 기준 ${k.pct}%`, gap: vnm - maxVnm, vnm, blocking: [...nonOrig].sort((a, b) => num(b.amount) - num(a.amount)) };
    }
    return { ok: null, why: "알 수 없는 기준" };
  };

  const results = rule.options.map((opt) => {
    const ev = opt.map((k) => ({ k, ...evalKind(k) }));
    const ok = ev.some((e) => e.ok === false) ? false : ev.some((e) => e.ok == null) ? null : true;
    return { opt, ev, ok };
  });
  const lines = results.map((r, i) => `${rule.options.length > 1 ? `선택 ${i + 1} ` : ""}${r.opt.map(kindText).join(" + ")}: ${r.ok === true ? "충족" : r.ok === false ? "미충족" : "판정 불가"} — ${r.ev.map((e) => e.why).join(" · ")}`);
  const met = results.find((r) => r.ok === true);
  if (met) return { status: "met", conditional: rule.exceptions, lines, blocking: [] };
  if (results.some((r) => r.ok == null)) return { status: "unknown", lines, blocking: [] };

  // 미충족 — 걸리는 재료가 가장 적은 선택지로 조달 대안을 낸다.
  const best = results
    .map((r) => {
      const bl = new Map();
      let gap = 0;
      for (const e of r.ev) {
        if (e.ok !== false) continue;
        if (e.k.type === "RVC") {
          gap = e.gap;
          let cut = 0;
          for (const m of e.blocking) { if (cut >= gap) break; bl.set(m, true); cut += num(m.amount) || 0; }
        } else (e.blocking || []).forEach((m) => bl.set(m, true));
      }
      return { r, blocking: [...bl.keys()], gap };
    })
    .sort((a, b) => a.blocking.length - b.blocking.length)[0];
  const names = best.blocking.map((m) => `${m.name || "(이름 없음)"}(${m.hs || "세번 없음"}, ${m.country || "원산지 없음"})`);
  const others = members.filter((c) => c !== "한국").slice(0, 12).join("·");
  const suggestion = `${names.join(", ")}을(를) 협정 당사국(한국·${others})에서 원산지 재료로 조달하면 ${best.r.opt.map(kindText).join(" + ")} 기준을 충족할 수 있습니다`
    + (best.gap > 0 ? ` (비원산지 재료를 ${Math.ceil(best.gap).toLocaleString()} 이상 줄여야 함)` : "") + ".";
  return { status: "not_met", lines, blocking: best.blocking, suggestion };
}

/** 세관장확인 요건 원문의 '●' 대상 항목과 품명의 겹치는 말(2글자 이상). 해당 여부 판정 아님. */
export function matchRequirement(fullText, description) {
  const items = String(fullText || "").split(/●/).slice(1)
    .map((s) => s.split(/\n|※/)[0].replace(/[\s.]+$/, "").trim()).filter(Boolean);
  const words = String(description || "").toLowerCase().split(/[^0-9a-z가-힣]+/).filter((w) => w.length >= 2);
  const hits = [];
  for (const it of items) {
    const low = it.toLowerCase();
    const w = words.filter((x) => low.includes(x) || (x.length >= 3 && low.includes(x.slice(-2)) && /[가-힣]$/.test(x)));
    if (w.length) hits.push({ item: it.slice(0, 80), words: Array.from(new Set(w)) });
  }
  return { items: items.length, hits: hits.slice(0, 3) };
}

/** 감면 셀 원문 → [{kind, codes, use, post}] */
export function parseExempt(raw) {
  if (!raw) return [];
  return String(raw).split(/\s\/\s/).map((seg) => {
    const kind = (seg.match(/^([^[]+?)\s*\[/) || [])[1] || seg.slice(0, 20);
    const codes = ((seg.match(/\[세종\]\s*([^[]+)/) || [])[1] || "").trim().replace(/\s*,\s*/g, ", ");
    const use = ((seg.match(/\[용도·규격\]\s*([^[]+)/) || [])[1] || "").trim();
    const post = ((seg.match(/\[사후관리\]\s*([A-Z])/) || [])[1] || "");
    return { kind: kind.trim(), codes, use, post };
  }).filter((e) => e.kind);
}

/**
 * 보고서 fta_origin 페이로드 — 협정별 세율·원산지결정기준·기준 유형(+BOM 이 있으면 판정)과 추천 세율.
 *   추천: BOM 판정이 있으면 '충족' 협정 중 가장 낮은 세율, 없으면 기준이 있는 협정 중 가장 낮은 세율(원산지 요건 충족 시).
 */
export function buildFtaOrigin({ hsk, rates, psrs, bom, price, enduse = {} }) {
  const hasBom = (bom || []).some((m) => m && (m.hs || m.amount));
  const rows = REPORT_AGREEMENTS.map((a) => {
    const psr = (psrs && psrs[a.ftaId]) || null;
    const text = psr && psr.available ? (psr.psr_ko || ((psr.rows || [])[0] || {}).criterion_ko || "") : "";
    const rate = rates && rates[a.rateCode] != null ? rates[a.rateCode] : null;
    const rule = parsePsr(text);
    const check = text && hasBom ? checkOrigin({ hsk, price, bom, psrText: text, members: a.members }) : null;
    const use = !!(enduse && enduse[a.rateCode]);
    return {
      name: a.name,
      rate: rate == null ? "" : `${rate}%${use ? " (용도세율)" : ""}`,
      enduse: use,
      rate_value: rate,
      psr: text,
      kinds: rule.options.map((o) => o.map(kindText).join(" + ")),
      exceptions: rule.exceptions,
      check: check && {
        status: check.status, conditional: !!check.conditional, lines: check.lines, suggestion: check.suggestion || "",
        blocking: (check.blocking || []).map((m) => ({ name: m.name || "", hs: String(m.hs || "").replace(/\D/g, "").slice(0, 6) })),
      },
    };
  });
  // 용도세율인 협정세율은 추천에서 뺀다(용도 요건이 따로 있어 원산지만으로 적용되지 않는다).
  const pool = rows.filter((r) => r.rate_value != null && r.psr && !r.enduse && (!hasBom || (r.check && r.check.status === "met")));
  pool.sort((x, y) => x.rate_value - y.rate_value);
  const best = pool[0];
  const recommend = best
    ? { name: best.name, rate: best.rate, basis: hasBom ? "BOM 기준 원산지 요건 충족" : "원산지 요건 충족 시 (BOM 미입력)" }
    : hasBom ? { name: "", rate: "", basis: "BOM 기준으로 원산지 요건을 충족하는 협정이 없습니다" } : null;
  if (!rows.some((r) => r.psr || r.rate)) return null;
  return { rows: rows.map(({ rate_value, enduse: _u, ...r }) => r), recommend, bom_used: hasBom, price: hasBom ? String(price || "") : "" };
}

/** 걸리는 재료(6자리)들 — 수입 실적을 불러올 대상. */
export function blockingHs6(fo) {
  const set = new Set();
  for (const r of (fo && fo.rows) || []) for (const m of (r.check && r.check.blocking) || []) if (/^\d{6}$/.test(m.hs)) set.add(m.hs);
  return [...set];
}

/**
 * 공급망 대안(5번) — 걸리는 재료마다 그 협정 당사국 중 우리나라 최근 12개월 수입액이 큰 나라(최대 3곳).
 *   stats = { hs6: {available, countries:[{name, import_usd}], period} }. 통계가 없으면 붙이지 않는다.
 */
export function attachAlternatives(fo, stats) {
  if (!fo) return fo;
  const rows = fo.rows.map((r) => {
    const rest = { ...r };
    const members = (REPORT_AGREEMENTS.find((a) => a.name === r.name) || {}).members || [];
    const bl = (r.check && r.check.blocking) || [];
    if (!bl.length) return rest;
    const alternatives = bl.map((m) => {
      const st = stats && stats[m.hs];
      if (!st || !st.available) return null;
      const countries = (st.countries || [])
        .filter((c) => members.some((x) => x !== "한국" && (c.name === x || c.name.includes(x) || x.includes(c.name))))
        .slice(0, 3)
        .map((c) => ({ name: c.name, import_usd: c.import_usd }));
      return { material: m.name, hs6: m.hs, countries, period: st.period ? `${st.period.from}~${st.period.to}` : "" };
    }).filter(Boolean);
    return alternatives.length ? { ...rest, check: { ...rest.check, alternatives } } : rest;
  });
  return { ...fo, rows };
}

